import { useState, useEffect } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const STEPS = [
  {
    id:       "account",
    icon:     "✅",
    title:    "Create your account",
    desc:     "Done — your 14-day free trial is active",
    detail:   "Your trial includes the Starter tools + the free ROAS Calculator. No credit card needed. Upgrade anytime to unlock Advanced features.",
    action:   null,
    autoCheck:true,
  },
  {
    id:       "profit",
    icon:     "💰",
    title:    "See your real profit (start here!)",
    desc:     "Upload one Myntra report → see profit on every order",
    detail:   "This is the heart of 3June. Go to Channels → Myntra Reports and upload your orders + settlement CSV (from Myntra Partner Portal → Finance). In 2 minutes you'll see exactly how much you actually kept on each order — after commission, logistics, ads and GST.",
    action:   { label:"Upload Myntra report →", page:"myntraimport" },
    autoCheck:false,
    highlight:true,
  },
  {
    id:       "profile",
    icon:     "🏪",
    title:    "Add your business details",
    desc:     "Store name, GSTIN, and address",
    detail:   "These appear on your GST invoices and shipping labels. Takes 2 minutes.",
    action:   { label:"Go to Profile →", page:"profile" },
    autoCheck:false,
  },
  {
    id:       "shopify",
    icon:     "🛍️",
    title:    "Connect your store (optional)",
    desc:     "Auto-sync orders from Shopify",
    detail:   "Want orders to flow in automatically instead of uploading? Go to Settings → Channels → Shopify and connect your store. You can also upload reports manually without connecting anything.",
    action:   { label:"Connect Shopify →", page:"channels" },
    autoCheck:false,
  },
  {
    id:       "reconcile",
    icon:     "🔍",
    title:    "Catch money you're owed",
    desc:     "Match marketplace payouts vs what hit your bank",
    detail:   "Go to Finance → Reconciliation. Upload your Myntra settlement report and 3June flags any orders where the platform paid you less than they owed.",
    action:   { label:"Check Reconciliation →", page:"reconciliation" },
    autoCheck:false,
  },
  {
    id:       "gst",
    icon:     "🧾",
    title:    "Get your GST report",
    desc:     "Consolidated GST across all platforms, ready to file",
    detail:   "3June auto-calculates CGST, SGST and IGST per order. Go to Finance → GST to see your first report, then export to CSV or Tally.",
    action:   { label:"View GST →", page:"gst" },
    autoCheck:false,
  },
];

const TIPS = [
  { icon:"💡", tip:"Sync your platforms first — everything else depends on your data being up to date" },
  { icon:"🏷️", tip:"Set low stock alerts (Settings in Inventory Pro) to never go out of stock again" },
  { icon:"💰", tip:"Check Finance → P&L every month — most sellers are surprised by their actual margins" },
  { icon:"🤖", tip:"Use AI Generate Tasks every morning — it reads your data and creates today's to-do list automatically" },
  { icon:"📊", tip:"RFM Analysis in Customer Pro tells you exactly who to contact for re-orders" },
  { icon:"🚚", tip:"Use Courier Rate Comparison in Orders Pro before every dispatch to save ₹15-40 per shipment" },
];

export default function PageGetStarted({ toast, tenant, products, orders, onNavigate }) {
  const [completed, setCompleted] = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_onboarding") || "{}"); } catch { return {}; }
  });

  // Auto-check account step
  useEffect(() => {
    if (!completed.account) {
      const updated = { ...completed, account: true };
      setCompleted(updated);
      localStorage.setItem("3june_onboarding", JSON.stringify(updated));
    }
    // Auto-check if platforms connected
    const plat = tenant?.platforms || {};
    if (plat.shopify?.connected && !completed.shopify) {
      toggle("shopify", true);
    }
    if (plat.amazon?.connected && !completed.amazon) {
      toggle("amazon", true);
    }
    // Auto-check if products loaded
    if (products?.length > 0 && !completed.inventory) {
      toggle("inventory", true);
    }
    // Auto-check if orders loaded
    if (orders?.length > 0 && !completed.inventory) {
      toggle("inventory", true);
    }
  }, [tenant, products, orders]);

  const toggle = (id, forceTrue) => {
    const updated = { ...completed, [id]: forceTrue !== undefined ? forceTrue : !completed[id] };
    setCompleted(updated);
    localStorage.setItem("3june_onboarding", JSON.stringify(updated));
  };

  const doneCount = STEPS.filter(s => completed[s.id]).length;
  const pct       = Math.round(doneCount / STEPS.length * 100);

  const trialDaysLeft = tenant?.trialEndsAt
    ? Math.max(0, Math.ceil((new Date(tenant.trialEndsAt) - Date.now()) / 86400000))
    : 14;

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom:20 }}>
        <div style={{ fontWeight:700, fontSize:20, marginBottom:4 }}>
          👋 Welcome to 3June{tenant?.storeName ? `, ${tenant.storeName}` : ""}!
        </div>
        <div style={{ fontSize:13, color:"#6b7280" }}>
          Let's find out how much profit you're really making. Start with the highlighted step — upload one Myntra report and see your numbers in 2 minutes.
        </div>
      </div>

      {/* Trial banner */}
      <div style={{ background:"linear-gradient(135deg,#F47920,#f59e0b)", borderRadius:12, padding:"16px 24px", marginBottom:20, display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
        <div>
          <div style={{ color:"#fff", fontWeight:700, fontSize:15, marginBottom:4 }}>🎁 Free Trial Active — {trialDaysLeft} days remaining</div>
          <div style={{ color:"rgba(255,255,255,0.85)", fontSize:12 }}>All modules unlocked · No credit card needed · Cancel anytime</div>
        </div>
        <div style={{ textAlign:"center" }}>
          <div style={{ color:"#fff", fontSize:28, fontWeight:800 }}>{pct}%</div>
          <div style={{ color:"rgba(255,255,255,0.8)", fontSize:11 }}>setup complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px", marginBottom:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:8 }}>
          <span style={{ fontWeight:600 }}>{doneCount} of {STEPS.length} steps completed</span>
          <span style={{ color:"#9ca3af" }}>{STEPS.length - doneCount} remaining</span>
        </div>
        <div style={{ height:8, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
          <div style={{ height:8, background:"linear-gradient(90deg,#F47920,#f59e0b)", width:pct+"%", borderRadius:4, transition:"width .5s" }}/>
        </div>
      </div>

      {/* Steps */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
        {STEPS.map((step, i) => {
          const done = !!completed[step.id];
          return (
            <div key={step.id} style={{ background:step.highlight&&!done?"#fffaf5":"#fff", borderRadius:10, border:"1px solid "+(done?"#bbf7d0":step.highlight?"#F47920":"#e5e7eb"), padding:"16px", display:"flex", gap:14, alignItems:"flex-start", borderLeft:"3px solid "+(done?"#15803d":step.highlight?"#F47920":"#e5e7eb"), boxShadow:step.highlight&&!done?"0 4px 14px rgba(244,121,32,0.12)":"none", gridColumn:step.highlight?"1 / -1":"auto" }}>
              {/* Checkbox */}
              <div onClick={() => toggle(step.id)} style={{ width:22, height:22, borderRadius:6, border:"2px solid "+(done?"#15803d":"#d1d5db"), background:done?"#15803d":"#fff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", flexShrink:0, marginTop:1 }}>
                {done && <span style={{ color:"#fff", fontSize:13, lineHeight:1 }}>✓</span>}
              </div>

              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                  <span style={{ fontSize:16 }}>{step.icon}</span>
                  <span style={{ fontWeight:700, fontSize:13, color:done?"#15803d":"#1A3A5C" }}>{step.title}</span>
                  {step.id === "account" && <span style={{ fontSize:9, background:"#f0fdf4", color:"#15803d", padding:"1px 6px", borderRadius:8, fontWeight:700 }}>DONE</span>}
                  {step.highlight && !done && <span style={{ fontSize:9, background:"#F47920", color:"#fff", padding:"1px 8px", borderRadius:8, fontWeight:700, letterSpacing:"0.5px" }}>START HERE</span>}
                </div>
                <div style={{ fontSize:12, color:done?"#15803d":"#6b7280", marginBottom:8 }}>{step.desc}</div>
                <div style={{ fontSize:11, color:"#9ca3af", lineHeight:1.5, marginBottom:step.action?10:0 }}>{step.detail}</div>
                {step.action && !done && onNavigate && (
                  <button style={{ padding:"5px 12px", borderRadius:6, fontSize:11, cursor:"pointer", background:"#F47920", color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }}
                    onClick={() => { onNavigate(step.action.page); toggle(step.id); }}>
                    {step.action.label}
                  </button>
                )}
                {done && step.action && (
                  <span style={{ fontSize:11, color:"#15803d", fontWeight:600 }}>✅ Completed</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Pro tips */}
      <div style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", overflow:"hidden" }}>
        <div style={{ padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13 }}>
          🚀 Pro Tips — Get the most out of 3June
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:0 }}>
          {TIPS.map((tip, i) => (
            <div key={i} style={{ padding:"12px 16px", borderBottom:i<TIPS.length-2?"1px solid #f3f4f6":"none", borderRight:i%2===0?"1px solid #f3f4f6":"none" }}>
              <span style={{ fontSize:16, marginRight:8 }}>{tip.icon}</span>
              <span style={{ fontSize:12, color:"#374151", lineHeight:1.5 }}>{tip.tip}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Support */}
      <div style={{ marginTop:16, background:"#f9fafb", borderRadius:10, border:"1px solid #e5e7eb", padding:"16px", display:"flex", gap:20, flexWrap:"wrap", alignItems:"center" }}>
        <div>
          <div style={{ fontWeight:700, fontSize:13, marginBottom:4 }}>Need help getting started?</div>
          <div style={{ fontSize:12, color:"#6b7280" }}>Our team is available Mon–Sat, 10am–7pm IST</div>
        </div>
        <div style={{ display:"flex", gap:10, marginLeft:"auto", flexWrap:"wrap" }}>
          <a href="https://wa.me/918287254548" target="_blank" rel="noreferrer"
            style={{ padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", background:"#25d366", color:"#fff", textDecoration:"none", fontWeight:600 }}>
            💬 WhatsApp Support
          </a>
          <a href="mailto:info@3june.in"
            style={{ padding:"8px 16px", borderRadius:8, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", textDecoration:"none", border:"1px solid #d1d5db", fontWeight:600 }}>
            📧 Email Us
          </a>
        </div>
      </div>
    </div>
  );
}
